import React, { useCallback, useState } from "react";
import { Stack } from "@mui/material";
import { toast } from "react-toastify";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { getScheduledDeliveriesForCpsGroup } from "@smartrr/shared/utils/getScheduledDeliveries";
import { toViewLongDate } from "@smartrr/shared/utils/renderViewDate";
import { localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { getGift } from "@customer-app/account/views/OperationAquarius/utils";
import {
  skipDelivery,
  stopDeliveryLoading,
  unskipDelivery,
} from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";
import { isCPSPrepaid, isCPSRecentlyMigratedPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { ICancellationReason, IPauseReason, IUserPause } from "@smartrr/shared/entities/CancellationRelationship";
import { saveUserPauseReasonAPI } from "@customer-app/state/actionCreators/retention";
import { captureException } from "@smartrr/shared/utils/captureException";
import { CenterText } from "./CenterText";
import PauseReasonPicker from "./PauseReasonPicker";

export interface ISkipViewProps {
  setIconButtonInactive: () => void;
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  saveUserCancellationData?: (cancel?: boolean) => void;
  cancellationReason?: ICancellationReason | undefined;
}

export const SkipView = ({
  delivery,
  setIconButtonInactive,
  saveUserCancellationData,
  cancellationReason,
}: ISkipViewProps): JSX.Element => {
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const organization = useSmartrrCustomerSelector(state => state.auth.organization);
  const isSkipLoading = useSmartrrCustomerSelector(state => state.purchaseStates.isSkipLoading);

  const [selectedPauseReason, setSelectedPauseReason] = useState<IPauseReason>();
  const [userPauseReasonText, setUserPauseReasonText] = useState<string>();
  const { isEnabled: hasRetention, pauseReasons } = useSmartrrCustomerSelector(state => state.retention);
  const isRetentionAvailable =
    hasRetention &&
    pauseReasons?.length &&
    !!organization?.hasRetentionPauseReasonEnabled &&
    !!organization?.hasRetentionSkipReasonEnabled;

  const localizeShorthand = "subscriptions.yourSubscriptions.orderOptionsModal";
  const { purchaseState, date } = delivery;

  const skdIdx = delivery.indexFromScheduleStart;
  const scheduledOrders = getScheduledDeliveriesForCpsGroup(organization!, [purchaseState], 7);
  const nextOrderDate =
    scheduledOrders[scheduledOrders.findIndex(ele => ele[1][0].indexFromNext === delivery.indexFromNext) + 1][0];

  const gift = getGift(purchaseState, skdIdx);

  const isPrepaid = isCPSPrepaid(purchaseState.schedule);
  const isRecentlyMigratedPrepaid = isCPSRecentlyMigratedPrepaid(purchaseState);
  const isPaid = !delivery.paymentMultipleDueOnDate;
  const orderFinalized = isPrepaid || isPaid;
  const skipAvailable = !orderFinalized || isPrepaid || (!isRecentlyMigratedPrepaid && !gift);

  const onSkip = async () => {
    await dispatch(skipDelivery(purchaseState.id, date))
      .then(res => {
        if (res.type === "SKIP_DELIVERY_SUCCESS") {
          toast(localize(`${localizeShorthand}.skipView.skippedToast`));
        } else if (res.type === "SKIP_DELIVERY_ERROR") {
          toast.error(localize(`${localizeShorthand}.skipView.errorSkipToast`));
        }
      })
      .finally(() => closeModal());
  };

  const onUnskip = async () => {
    await dispatch(unskipDelivery(purchaseState.id, date))
      .then(res => {
        if (res.type === "UNSKIP_DELIVERY_SUCCESS") {
          toast(localize(`${localizeShorthand}.skipView.unskippedToast`));
        } else if (res.type === "UNSKIP_DELIVERY_ERROR") {
          toast.error(localize(`${localizeShorthand}.skipView.errorUnskipToast`));
        }
      })
      .finally(() => closeModal());
  };

  const onToggleSkip = async () => {
    const returnDeliveryAction = () => (delivery.isSkipped ? onUnskip() : onSkip());
    await returnDeliveryAction();
    // Using setTimeout and stopDeliveryLoading because otherwise in some reason Confirm btn isLoading prop isn't applied properly, so confirm btn can be clicked twice even if it's disabled/loading and onToggleSkip is repeated after it's enabled
    setTimeout(() => dispatch(stopDeliveryLoading()), 0);
    if (cancellationReason && saveUserCancellationData) {
      saveUserCancellationData();
    }
    if (isRetentionAvailable) {
      await saveUserPauseData(userPauseReasonText || "", selectedPauseReason?.id);
    }
  };

  const showReasonPicker = isRetentionAvailable && !delivery.isSkipped && !cancellationReason;

  const saveUserPauseData = useCallback(
    async (userPauseReason: string, id?: string) => {
      const pcs = delivery.purchaseState;
      const userPause: IUserPause = {
        reasonText: userPauseReason || "",
        stId: pcs.id,
        pauseReasonId: id || "",
      };

      await saveUserPauseReasonAPI(userPause)
        .then(res => {
          if (res.type === "error") {
            captureException("Error saving user's pause reason", res.message);
          }
        })
        .catch(error => {
          captureException("Error saving user's pause reason", error);
        });
    },
    [userPauseReasonText, selectedPauseReason]
  );
  return (
    <React.Fragment>
      <CenterText title={localize(`${localizeShorthand}.skipView.${delivery.isSkipped ? "unskip" : "skip"}`)}>
        {localize(
          `${localizeShorthand}.skipView.${delivery.isSkipped || skdIdx !== 0 ? "unskipBody" : "skipBody"}`
        )}
        <br />
        <span style={{ fontWeight: 700 }}>{toViewLongDate(delivery.isSkipped ? date : nextOrderDate)}</span>
      </CenterText>
      {showReasonPicker ? (
        <Stack spacing="15px" justifyContent="center" alignItems="center" sx={{ marginTop: "5px" }}>
          <CenterText title={localize(`${localizeShorthand}.skipView.reasonTitle`)}></CenterText>
          <PauseReasonPicker
            selectedPauseReason={selectedPauseReason}
            setSelectedPauseReason={setSelectedPauseReason}
            userPauseReasonText={userPauseReasonText}
            setUserPauseReasonText={setUserPauseReasonText}
            placeholderText={localize(`${localizeShorthand}.skipView.textAreaPlaceholder`)}
          />
        </Stack>
      ) : null}
      <ConfirmCancelButtons
        onCancel={setIconButtonInactive}
        onConfirm={onToggleSkip}
        isLoading={isSkipLoading}
        showConfirmButton={skipAvailable}
      />
    </React.Fragment>
  );
};
